import { zodResolver } from '@hookform/resolvers/zod';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DiscountAndFee, useProducts } from '../../../data-access';
import {
  ModifierQueryInputs,
  ModifierQuerySchema,
} from '../components/ModifierQueryForm';

interface ModifierQueryContextValue {
  searchTerm: string;
  sortOrder: string;
  rebatesFiltered: DiscountAndFee[];
}

const ModifierQueryContext = createContext<
  ModifierQueryContextValue | undefined
>(undefined);

export const ModifierQueryProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { availableRebates } = useProducts();

  const modifierQueryForm = useForm<ModifierQueryInputs>({
    defaultValues: { searchTerm: '', sortOrder: 'desc' },
    resolver: zodResolver(ModifierQuerySchema),
  });

  const searchTerm = modifierQueryForm.watch('searchTerm');
  const sortOrder = modifierQueryForm.watch('sortOrder');

  const rebatesFiltered = useMemo(
    () =>
      availableRebates
        .filter((d) =>
          [d.description, d.displayName]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
        .sort(
          (a, b) =>
            (sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount) ||
            a.displayName.localeCompare(b.displayName)
        ),
    [availableRebates, searchTerm, sortOrder]
  );

  return (
    <ModifierQueryContext.Provider
      value={{ searchTerm, sortOrder, rebatesFiltered }}
    >
      <FormProvider {...modifierQueryForm}>{children}</FormProvider>
    </ModifierQueryContext.Provider>
  );
};

export const useModifierQuery = () => {
  const context = useContext(ModifierQueryContext);
  if (context === undefined) {
    throw new Error(
      'useModifierQuery must be used within a ModifierQueryProvider'
    );
  }
  return context;
};
